/**
 * Top-level React error boundary.
 *
 * Catches render-time errors anywhere below it, reports them to the backend
 * (`logClientError` → Telegram, same channel as the global handlers in
 * `errorReporting.ts`), and shows a minimal recovery screen instead of a blank
 * white page. Styling lives in `theme-utilities.css` (`.error-boundary-fallback`)
 * so it renders without depending on the (possibly broken) component tree.
 *
 * The screen offers a one-tap "Copy error details" button so a player can hand the
 * full stack + environment to a developer without opening devtools. This boundary
 * sits ABOVE `ToastProvider` (see `main.tsx`), so it can't use the app toast — the
 * copy confirmation is a self-contained inline state instead.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logClientError } from '../api/clientLogApi';
import { SUIKAMON_APP_VERSION } from '@shared/domain/appConfig';
import { isPageTranslated } from '../lib/domMutationGuard';
import { translate } from '../i18n/translate';
import { claimChunkReload, isChunkLoadError, reloadPageAfter } from '../utils/lazyWithReload';

/** Reload-guard key for stale chunks that reach the boundary without going through `lazyWithReload`. */
const BOUNDARY_CHUNK_KEY = 'ErrorBoundary';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  details: string;
  copied: boolean;
}

/** Build a copy-pasteable diagnostic block from the caught error + environment. */
function buildErrorDetails(error: Error, info: ErrorInfo): string {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const win = typeof window !== 'undefined' ? window : undefined;
  const lines = [
    `Suikamon ${SUIKAMON_APP_VERSION}`,
    `Time: ${new Date().toISOString()}`,
    `Page: ${win ? win.location.href : 'n/a'}`,
    `User agent: ${nav ? nav.userAgent : 'n/a'}`,
    `Viewport: ${win ? `${win.innerWidth}x${win.innerHeight}` : 'n/a'}`,
    `Translated: ${isPageTranslated()}`,
    '',
    `Error: ${error.name}: ${error.message}`,
    '',
    'Stack:',
    error.stack ?? '(no stack)',
    '',
    'Component stack:',
    info.componentStack ?? '(no component stack)',
  ];
  return lines.join('\n');
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, details: '', copied: false };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // A stale chunk after a deploy is not a bug: fetch the new build instead of showing an
    // error screen and paging the team. Only once the guarded reloads are used up is it reported.
    if (isChunkLoadError(error)) {
      const delayMs = claimChunkReload(BOUNDARY_CHUNK_KEY);
      if (delayMs !== null) {
        reloadPageAfter(delayMs);
        return;
      }
    }
    const page = typeof window !== 'undefined' ? window.location.href : 'n/a';
    const context = `page=${page}\ntranslated=${isPageTranslated()}\n${error.stack ?? ''}\nComponent stack:${info.componentStack ?? ''}`;
    logClientError(`[Render] ${error.message || error.name}`, context);
    this.setState({ details: buildErrorDetails(error, info) });
  }

  private handleReload = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  private handleCopy = async (): Promise<void> => {
    const { details } = this.state;
    if (!details) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(details);
      } else {
        // Fallback for insecure contexts / older WebViews without the async API.
        const textarea = document.createElement('textarea');
        textarea.value = details;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      this.setState({ copied: true });
      window.setTimeout(() => this.setState({ copied: false }), 2500);
    } catch {
      // Last resort: surface the details so the user can select/copy manually.
      window.prompt(translate('errors.boundary.copyPrompt'), details);
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-boundary-fallback">
          <h1>{translate('errors.boundary.title')}</h1>
          <p>
            {translate('errors.boundary.text')}
          </p>
          <div className="error-boundary-fallback__actions">
            <button type="button" onClick={this.handleReload}>
              {translate('errors.boundary.reload')}
            </button>
            {this.state.details && (
              <button
                type="button"
                className="error-boundary-fallback__copy"
                onClick={this.handleCopy}
                aria-live="polite"
              >
                {this.state.copied ? translate('errors.boundary.copied') : translate('errors.boundary.copy')}
              </button>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
